import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { IconMagic, IconSettings } from '../Icons';
import { TzBinding, TzTemplate, effectiveTzTemplate } from './tzResolve';
import './TzGenerateBar.css';

type Props = {
  subjectId: number;
  sourceCode?: string;
  templates: TzTemplate[];
  bindings: TzBinding[];
  overrideId?: string;
  busy: boolean;
  step?: string;
  onOverride: (templateId: string) => void;
  onGenerate: (templateId: string) => void;
  onCancel: () => void;
  onGear: () => void;
  balance?: number | null;
};

type Box = { top?: number; bottom?: number; left: number; width: number; maxHeight: number };

function placeMenu(anchor: HTMLElement): Box {
  const rect = anchor.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - 12;
  const above = rect.top - 12;
  const openUp = below < 160 && above > below;
  const maxHeight = Math.max(80, Math.min(240, openUp ? above : below));
  const width = Math.max(rect.width, 220);
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
  if (openUp) return { bottom: window.innerHeight - rect.top + 4, left, width, maxHeight };
  return { top: rect.bottom + 4, left, width, maxHeight };
}

function rub(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '';
  const digits = Math.abs(value) >= 100 ? 0 : 2;
  return `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)} ₽`;
}

const TzGenerateBar: React.FC<Props> = ({
  subjectId,
  sourceCode,
  templates,
  bindings,
  overrideId,
  busy,
  step,
  onOverride,
  onGenerate,
  onCancel,
  onGear,
  balance,
}) => {
  const template = effectiveTzTemplate(templates, bindings, subjectId, overrideId, sourceCode || 'wb');
  const anchor = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<Box | null>(null);
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  useEffect(() => {
    if (!open || !anchor.current) return;
    const update = () => {
      if (anchor.current) setBox(placeMenu(anchor.current));
    };
    update();
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchor.current?.contains(target) || menu.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openList = () => {
    if (templates.length === 0) {
      onGear();
      return;
    }
    setOpen((value) => !value);
  };

  const label = template?.name || (templates.length === 0 ? 'Создать шаблон' : 'Выбрать шаблон');

  return (
    <div className="tz-bar" onClick={stop}>
      <button
        ref={anchor}
        type="button"
        className="tz-bar-name"
        disabled={busy}
        title={overrideId ? 'Только эта строка' : 'Шаблон для заполнения'}
        onClick={openList}
      >
        {overrideId && <i className="tz-once" />}
        <span>{label}</span>
        {balance != null && <b className="tz-bar-balance" title="Списано за заполнение карточек">{rub(balance)}</b>}
      </button>
      <button
        type="button"
        className={`tz-bar-run${busy ? ' is-busy' : ''}`}
        disabled={!busy && !template}
        title={busy ? 'Остановить генерацию' : (template ? 'Заполнить карточку по шаблону' : 'Сначала выберите шаблон')}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (busy) onCancel();
          else if (template) onGenerate(template.id);
        }}
      >
        {busy ? <Spinner size="sm" animation="border" /> : <IconMagic size={12} />}
        {busy ? (step || 'Пишем описание') : 'Заполнить'}
        {busy && <span className="tz-bar-stop">стоп</span>}
      </button>
      <button type="button" className="tz-bar-gear" title="Шаблоны и правила" onClick={onGear}>
        <IconSettings />
      </button>
      {open && box && createPortal(
        <ul
          ref={menu}
          className="tz-menu"
          style={{ top: box.top, bottom: box.bottom, left: box.left, width: box.width, maxHeight: box.maxHeight }}
        >
          {templates.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={item.id === template?.id ? 'is-on' : ''}
                onClick={() => {
                  onOverride(item.id);
                  setOpen(false);
                }}
              >
                {item.name}
                {!item.is_public && <small>только я</small>}
                {item.is_public && (item.score_count || 0) > 0 && <small>{item.score_avg}</small>}
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default TzGenerateBar;
